import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { 
  Star, Gift, Check, X, ExternalLink, Shield, Headphones, 
  CreditCard, Gamepad2, ChevronLeft, Trophy, Users, Send
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { StarRating, RatingBar } from "../components/StarRating";
import ReviewCard from "../components/ReviewCard";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CasinoDetail() {
  const { slug } = useParams();
  const [casino, setCasino] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [topCasinos, setTopCasinos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    user_name: "",
    user_email: "",
    rating: 5,
    title: "",
    content: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [casinoRes, reviewsRes] = await Promise.all([
          axios.get(`${API}/casinos/${slug}`),
          axios.get(`${API}/reviews?casino_id=all`),
          axios.get(`${API}/blogs`),
          axios.get(`${API}/casinos`),
        ]);
        setCasino(casinoRes.data);
        const blogsAll = await axios.get(`${API}/blogs`);
        setRelatedBlogs(blogsAll.data.slice(0, 3));
        const casinosAll = await axios.get(`${API}/casinos`);
        setTopCasinos(casinosAll.data.filter(c => c.slug !== slug).slice(0, 3));
        setReviews(reviewsRes.data.slice(0, 5));
      } catch (error) {
        console.error("Error fetching casino:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  useEffect(() => {
    if (!casino) return;
    // Meta tags
    document.title = `${casino.name} Review Canada 2025 - Bonus, Rating & More | PlaySpiro`;
    const setMeta = (name, content, prop) => {
      let el = document.querySelector(prop ? `meta[property="${name}"]` : `meta[name="${name}"]`);
      if (!el) { el = document.createElement('meta'); prop ? el.setAttribute('property', name) : el.setAttribute('name', name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', `${casino.name} honest review for Canadian players. ${casino.bonus_description}. Rating: ${casino.rating}/5. Read before you play.`);
    setMeta('og:title', `${casino.name} Review Canada 2025 | PlaySpiro`, true);
    setMeta('og:description', casino.bonus_description, true);
    setMeta('og:url', `https://playspiro.com/casino/${slug}`, true);
    setMeta('og:type', 'article', true);
    // JSON-LD structured data
    const jsonLd = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Review",
          "itemReviewed": {
            "@type": "LocalBusiness",
            "name": casino.name,
            "url": casino.website_url || `https://playspiro.com/casino/${slug}`,
            "image": casino.logo,
            "description": casino.bonus_description,
            "priceRange": casino.min_deposit ? `Min deposit: ${casino.min_deposit}` : "$$"
          },
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": casino.rating,
            "bestRating": "5",
            "worstRating": "1"
          },
          "name": `${casino.name} Review`,
          "author": { "@type": "Organization", "name": "PlaySpiro" },
          "publisher": { "@type": "Organization", "name": "PlaySpiro", "url": "https://playspiro.com" },
          "datePublished": "2025-01-01",
          "dateModified": new Date().toISOString().split('T')[0],
          "description": casino.final_verdict || casino.bonus_description
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": `Is ${casino.name} legit and safe for Canadian players?`,
              "acceptedAnswer": { "@type": "Answer", "text": casino.security_info || `${casino.name} is a licensed and regulated online casino. Always gamble responsibly.` }
            },
            {
              "@type": "Question",
              "name": `What is the welcome bonus at ${casino.name}?`,
              "acceptedAnswer": { "@type": "Answer", "text": `${casino.name} offers ${casino.bonus_description}. Minimum deposit: ${casino.min_deposit}. Wagering requirement: ${casino.wagering}.` }
            },
            {
              "@type": "Question",
              "name": `What is the minimum deposit at ${casino.name}?`,
              "acceptedAnswer": { "@type": "Answer", "text": `The minimum deposit at ${casino.name} is ${casino.min_deposit}.` }
            },
            {
              "@type": "Question",
              "name": `How fast are withdrawals at ${casino.name}?`,
              "acceptedAnswer": { "@type": "Answer", "text": casino.support_info || `${casino.name} processes withdrawals within 24-72 hours depending on the payment method chosen.` }
            }
          ]
        }
      ]
    };
    let script = document.querySelector('#casino-jsonld');
    if (!script) { script = document.createElement('script'); script.id = 'casino-jsonld'; script.type = 'application/ld+json'; document.head.appendChild(script); }
    script.textContent = JSON.stringify(jsonLd);
    return () => {
      const s = document.querySelector('#casino-jsonld');
      if (s) s.remove();
    };
  }, [casino, slug]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.user_name || !reviewForm.user_email || !reviewForm.title || !reviewForm.content) {
      toast.error("Please fill in all fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        ...reviewForm,
        casino_id: casino.id,
      });
      toast.success("Review submitted! It will appear after moderation.");
      setReviewForm({ user_name: "", user_email: "", rating: 5, title: "", content: "" });
    } catch (error) {
      toast.error("Failed to submit review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="h-96 bg-card rounded-2xl animate-pulse" />
        </div>
      </div>
    );
  }

  if (!casino) {
    return (
      <div className="min-h-screen py-8 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Casino Not Found</h1>
          <Link to="/casinos">
            <Button className="gradient-neon text-white rounded-full">
              <ChevronLeft className="w-4 h-4 mr-2" /> Back to Casinos
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{casino ? `${casino.name} Review 2026 – PlaySpiro` : "Casino Review – PlaySpiro"}</title>
        <meta name="description" content={casino ? `Expert ${casino.name} review. Rating: ${casino.rating}/5. ${casino.bonus_description}.` : "Expert casino review at PlaySpiro."} />
        <link rel="canonical" href={`https://playspiro.com/casino/${casino?.slug || ""}`} />
        <meta property="og:title" content={casino ? `${casino.name} Review – PlaySpiro` : "Casino Review – PlaySpiro"} />
        <meta property="og:url" content={`https://playspiro.com/casino/${casino?.slug || ""}`} />
        <meta property="og:type" content="article" />
        {casino?.logo && <meta property="og:image" content={casino.logo} />}
      </Helmet>
    <div className="min-h-screen py-8" data-testid="casino-detail-page">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 mb-6">
        <Link to="/casinos" className="inline-flex items-center text-white/60 hover:text-primary transition-colors">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back to Casinos
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-4">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header Card */}
            <div className="glass rounded-2xl p-6 md:p-8">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl bg-white/5 overflow-hidden border border-white/10 flex-shrink-0">
                  <img 
                    src={casino.logo} 
                    alt={casino.name}
                    className="w-full h-full object-cover"
                   loading="lazy"/>
                </div>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <h1 className="text-3xl md:text-4xl font-black text-white font-['Montserrat']">
                      {casino.name}
                    </h1>
                    {casino.is_featured && (
                      <Badge className="badge-exclusive">Featured</Badge>
                    )}
                    {casino.is_trending && (
                      <Badge className="badge-trending">Trending</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mb-4">
                    <StarRating rating={casino.rating} size="lg" />
                    <span className="text-white/40 text-sm">({casino.review_count?.toLocaleString() || 0} reviews)</span>
                  </div>
                  <p className="text-white/70 mb-4">{casino.bonus_description}</p>
                  <div className="flex flex-wrap gap-3">
                    <a href={casino.website_url || "#"}  >
                      <Button className="btn-glow gradient-neon text-white font-bold rounded-full px-8" data-testid="visit-site-btn">
                        Visit Casino <ExternalLink className="w-4 h-4 ml-2" />
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Welcome Bonus */}
            <div className="glass rounded-2xl p-6 md:p-8">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3 font-['Montserrat']">
                <Gift className="w-6 h-6 text-secondary" /> Welcome Bonus
              </h2>
              <div className="bg-gradient-to-r from-primary/20 to-secondary/20 rounded-xl p-6 text-center mb-6">
                <p className="text-4xl md:text-5xl font-black text-secondary mb-2">{casino.bonus_amount}</p>
                <p className="text-white/60">+ {casino.free_spins} Free Spins</p>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="bg-white/5 rounded-lg p-4 text-center">
                  <p className="text-white/50 text-sm mb-1">Wagering</p>
                  <p className="text-white font-bold">{casino.wagering}</p>
                </div>
                <div className="bg-white/5 rounded-lg p-4 text-center">
                  <p className="text-white/50 text-sm mb-1">Min Deposit</p>
                  <p className="text-white font-bold">{casino.min_deposit}</p>
                </div>
                <div className="bg-white/5 rounded-lg p-4 text-center">
                  <p className="text-white/50 text-sm mb-1">Free Spins</p>
                  <p className="text-white font-bold">{casino.free_spins}</p>
                </div>
              </div>
            </div>

            {/* Pros & Cons */}
            <div className="glass rounded-2xl p-6 md:p-8">
              <h2 className="text-2xl font-bold text-white mb-6 font-['Montserrat']">Pros & Cons</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-green-400 font-semibold mb-4 flex items-center gap-2">
                    <Check className="w-5 h-5" /> Pros
                  </h3>
                  <ul className="space-y-3">
                    {casino.pros.map((pro, i) => (
                      <li key={i} className="flex items-start gap-3 text-white/80">
                        <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        {pro}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-red-400 font-semibold mb-4 flex items-center gap-2">
                    <X className="w-5 h-5" /> Cons
                  </h3>
                  <ul className="space-y-3">
                    {casino.cons.map((con, i) => (
                      <li key={i} className="flex items-start gap-3 text-white/80">
                        <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        {con}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Games */}
            <div className="glass rounded-2xl p-6 md:p-8">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3 font-['Montserrat']">
                <Gamepad2 className="w-6 h-6 text-primary" /> Games Available
              </h2>
              <div className="flex flex-wrap gap-2">
                {casino.games.map((game, i) => (
                  <Badge key={i} variant="secondary" className="bg-white/10 text-white px-4 py-2">
                    {game}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Payment Methods */}
            <div className="glass rounded-2xl p-6 md:p-8">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3 font-['Montserrat']">
                <CreditCard className="w-6 h-6 text-secondary" /> Payment Methods
              </h2>
              <div className="flex flex-wrap gap-2">
                {casino.payment_methods.map((method, i) => (
                  <Badge key={i} variant="outline" className="border-white/20 text-white px-4 py-2">
                    {method}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Security & Support */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="glass rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2 font-['Montserrat']">
                  <Shield className="w-5 h-5 text-green-400" /> Security
                </h3>
                <p className="text-white/70">{casino.security_info}</p>
              </div>
              <div className="glass rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2 font-['Montserrat']">
                  <Headphones className="w-5 h-5 text-primary" /> Support
                </h3>
                <p className="text-white/70">{casino.support_info}</p>
              </div>
            </div>

            {/* Final Verdict */}
            <div className="glass rounded-2xl p-6 md:p-8 border-2 border-primary/30">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3 font-['Montserrat']">
                <Trophy className="w-6 h-6 text-secondary" /> Final Verdict
              </h2>
              <p className="text-white/80 text-lg">{casino.final_verdict}</p>
            </div>

            {/* User Reviews */}
            <div className="glass rounded-2xl p-6 md:p-8">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3 font-['Montserrat']">
                <Users className="w-6 h-6 text-primary" /> Player Reviews
              </h2>
              {/* Rating Summary */}
              <div style={{background:"rgba(168,85,247,0.08)",border:"1px solid rgba(168,85,247,0.2)",borderRadius:"16px",padding:"20px",marginBottom:"24px",display:"flex",gap:"24px",alignItems:"center",flexWrap:"wrap"}}>
                <div style={{textAlign:"center",minWidth:"80px"}}>
                  <div style={{fontSize:"3rem",fontWeight:"bold",color:"#ffd700",lineHeight:1}}>{casino?.rating || "5.0"}</div>
                  <div style={{color:"rgba(255,255,255,0.5)",fontSize:"0.75rem",marginTop:"4px"}}>out of 5.0</div>
                  <div style={{display:"flex",gap:"2px",justifyContent:"center",marginTop:"6px"}}>
                    {[1,2,3,4,5].map(s => <span key={s} style={{color: s <= Math.round(casino?.rating||5) ? "#ffd700" : "rgba(255,255,255,0.2)",fontSize:"0.9rem"}}>★</span>)}
                  </div>
                </div>
                <div style={{flex:1,minWidth:"160px"}}>
                  {[5,4,3,2,1].map(star => {
                    const count = reviews.filter(r => Math.round(r.rating) === star).length;
                    const pct = reviews.length ? (count/reviews.length*100) : (star === 5 ? 70 : star === 4 ? 20 : star === 3 ? 7 : star === 2 ? 2 : 1);
                    return (
                      <div key={star} style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"4px"}}>
                        <span style={{color:"rgba(255,255,255,0.5)",fontSize:"0.75rem",width:"12px"}}>{star}</span>
                        <span style={{color:"#ffd700",fontSize:"0.75rem"}}>★</span>
                        <div style={{flex:1,height:"6px",background:"rgba(255,255,255,0.1)",borderRadius:"3px",overflow:"hidden"}}>
                          <div style={{width:`${pct}%`,height:"100%",background:"linear-gradient(90deg,#a855f7,#ffd700)",borderRadius:"3px",transition:"width 0.5s"}}/>
                        </div>
                        <span style={{color:"rgba(255,255,255,0.4)",fontSize:"0.7rem",width:"24px"}}>{Math.round(pct)}%</span>
                      </div>
                    );
                  })}
                </div>
                <div style={{textAlign:"center"}}>
                  <div style={{color:"rgba(255,255,255,0.5)",fontSize:"0.8rem"}}>{casino?.review_count?.toLocaleString() || reviews.length} reviews</div>
                  <div style={{color:"#22c55e",fontSize:"0.75rem",marginTop:"4px"}}>✓ Verified players</div>
                </div>
              </div>
              
              {reviews.length > 0 ? (
                <div className="space-y-4 mb-8">
                  {reviews.map((review) => (
                    <ReviewCard key={review.id} review={review} />
                  ))}
                </div>
              ) : (
                <p className="text-white/60 mb-8">No reviews yet. Be the first to review!</p>
              )}

              {/* Submit Review Form */}
              <div className="border-t border-white/10 pt-6">
                <h3 className="text-xl font-bold text-white mb-4">Write a Review</h3>
                <form onSubmit={handleSubmitReview} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      placeholder="Your Name"
                      value={reviewForm.user_name}
                      onChange={(e) => setReviewForm(prev => ({ ...prev, user_name: e.target.value }))}
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="review-name-input"
                    />
                    <Input
                      type="email"
                      placeholder="Your Email"
                      value={reviewForm.user_email}
                      onChange={(e) => setReviewForm(prev => ({ ...prev, user_email: e.target.value }))}
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="review-email-input"
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-white/60">Rating:</span>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setReviewForm(prev => ({ ...prev, rating: star }))}
                          className="p-1"
                        >
                          <Star 
                            className={`w-6 h-6 ${star <= reviewForm.rating ? 'star-filled fill-current' : 'star-empty'}`} 
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                  <Input
                    placeholder="Review Title"
                    value={reviewForm.title}
                    onChange={(e) => setReviewForm(prev => ({ ...prev, title: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="review-title-input"
                  />
                  <Textarea
                    placeholder="Write your review..."
                    value={reviewForm.content}
                    onChange={(e) => setReviewForm(prev => ({ ...prev, content: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white min-h-[120px]"
                    data-testid="review-content-input"
                  />
                  <Button 
                    type="submit" 
                    disabled={submitting}
                    className="btn-glow gradient-neon text-white font-semibold rounded-full px-8"
                    data-testid="submit-review-btn"
                  >
                    {submitting ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" /> Submit Review
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6 lg:sticky lg:top-24 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto lg:pr-1">
            {/* Rating Breakdown */}
            <div className="glass rounded-2xl p-6">
              <h3 className="text-xl font-bold text-white mb-4 font-['Montserrat']">Rating Breakdown</h3>
              <div className="space-y-3">
                <RatingBar value={casino.rating} label="Overall" color="gold" />
                <RatingBar value={casino.rating - 0.2} label="Bonuses" color="primary" />
                <RatingBar value={casino.rating - 0.1} label="Games" color="blue" />
                <RatingBar value={casino.rating + 0.1 > 5 ? 5 : casino.rating + 0.1} label="Support" color="primary" />
                <RatingBar value={casino.rating - 0.3} label="Payouts" color="gold" />
              </div>

              {/* Quick Actions */}
              <div className="mt-6 pt-6 border-t border-white/10">
                <a href={casino.website_url || "#"}   className="block">
                  <Button className="w-full btn-glow gradient-neon text-white font-bold rounded-full mb-3">
                    Visit Casino
                  </Button>
                </a>
                <Link to="/complaint">
                  <Button variant="outline" className="w-full border-white/20 text-white rounded-full">
                    Report Issue
                  </Button>
                </Link>
              </div>
            </div>

            {/* Related Blog Posts */}
            {relatedBlogs.length > 0 && (
              <div className="glass rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-4 font-['Montserrat']">Casino Guides</h3>
                <div className="space-y-3">
                  {relatedBlogs.map(b => (
                    <Link key={b.id} to={`/blog/${b.slug}`} className="block group">
                      <p className="text-white/80 text-sm group-hover:text-primary transition-colors line-clamp-2">{b.title}</p>
                      <span className="text-white/40 text-xs">Read guide →</span>
                    </Link>
                  ))}
                </div>
                <Link to="/blog" className="text-primary text-xs mt-4 inline-block hover:underline">All casino guides →</Link>
              </div>
            )}

            {/* Compare Other Casinos */}
            {topCasinos.length > 0 && (
              <div className="glass rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-4 font-['Montserrat']">Compare Casinos</h3>
                <div className="space-y-3">
                  {topCasinos.map(c => (
                    <Link key={c.id} to={`/casino/${c.slug}`} className="flex items-center gap-3 group">
                      <img src={c.logo} alt={c.name} className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-white/80 text-sm group-hover:text-primary transition-colors truncate">{c.name}</p>
                        <p className="text-secondary text-xs">⭐ {c.rating}/5</p>
                      </div>
                    </Link>
                  ))}
                </div>
                <Link to="/casinos" className="text-primary text-xs mt-4 inline-block hover:underline">View all casinos →</Link>
              </div>
            )}

            {/* Bonuses CTA */}
            <div className="glass rounded-2xl p-6 text-center border border-secondary/20">
              <p className="text-white font-bold mb-1">Exclusive Bonuses</p>
              <p className="text-white/50 text-xs mb-4">Free spins & no deposit offers</p>
              <Link to="/bonuses">
                <button className="w-full gradient-neon text-white text-sm font-bold py-2 rounded-full">
                  Claim Bonus →
                </button>
              </Link>
            </div>

            {/* Ad */}
            <div className="ad-banner h-[250px] rounded-xl" data-testid="ad-sidebar-detail">
              <span>Ad 300x250</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}